import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { CommandResponse } from 'common/hooks/useCommandRequestV2'

import { Alert, Col, Radio, Row } from 'common/components'

import { BaseComponentProps } from '../../Common'

export type SetWaitingPeriodProps = {
  productClassId: string
  waitingPeriod?: number
}

const SetWaitingPeriod = (
  props: BaseComponentProps<SetWaitingPeriodProps> & {
    commandHandler?: (values: any) => Promise<CommandResponse<void>>
  },
) => {
  const { bindSubmitFormHandler } = props

  const { t: translate } = useTranslation()

  const [initialValues] = useState<{ waitingPeriod?: number }>({
    waitingPeriod: props.state.waitingPeriod,
  })

  const onSubmit = async (values: { waitingPeriod: number }) => {
    const result = await props.commandHandler({
      waitingPeriod: values.waitingPeriod,
      productClassId: props.state.productClassId,
    })

    if (result.completed) {
      props.finish()
    } else {
      props.setIsSubmiting(false)
    }
  }

  useEffect(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(initialValues))
  }, [initialValues])

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="set-perks-waiting-period-wrapper">
            <h5 className="mb-24">How long should new employees wait before becoming eligible for reimbursements?</h5>

            <Alert
              showIcon
              type="info"
              className={'mb-24'}
              message={'What is the waiting period?'}
              description={
                <>
                  <div>
                    A waiting period allows you to set a time after a new employee’s hire date before they accrue an
                    allowance and can use the HRA benefit. It’s commonly used as a probationary period. Regulations
                    prohibit waiting periods longer than 90 days.
                  </div>
                  <ul className="mt-4 mb-0 pl-16">
                    <li>
                      <span className="text-semi-bold">Note:</span> All employees will have a minimum of 30 days from
                      their ICHRA Notification Date in which they can make their ICHRA opt-in/out decision. This is
                      separate from but not in addition to any waiting period you might set.
                    </li>
                  </ul>
                </>
              }
            />

            <Radio.Group
              name="waitingPeriod"
              className="w-100 ichra-benefit-design-radio-group"
              defaultValue={props.state.waitingPeriod}
            >
              <Row gutter={10}>
                <Col span={24} className={'mb-8'}>
                  <Radio.Button value={0}>No waiting period</Radio.Button>
                </Col>
                <Col span={24} className={'mb-8'}>
                  <Radio.Button value={30}>30-day waiting period</Radio.Button>
                </Col>
                <Col span={24} className={'mb-8'}>
                  <Radio.Button value={60}>60-day waiting period</Radio.Button>
                </Col>
                <Col span={24}>
                  <Radio.Button value={90}>90-day waiting period</Radio.Button>
                </Col>
              </Row>
            </Radio.Group>
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    waitingPeriod: Yup.number()
      .required(translate('general.required'))
      .oneOf([0, 30, 60, 90])
      .typeError(translate('admin-dashboard.benefit-details.class-design.errors.enter-number')),
  })

export default SetWaitingPeriod
